import React, { useState } from "react";
import clsx from "clsx";
import { MagnifyingGlassIcon, XMarkIcon } from "@heroicons/react/24/outline";
import { Button } from "@/ui-components/button-with-loading";

type Props = {
  /** Drives the submit button's spinner + disabled state while a search runs. */
  loading?: boolean;
  /** Optional controlled value. When provided (together with onChange) the
   *  parent owns the text — needed so a panel's "Clear" button can reset the
   *  box. Omit both to keep the input uncontrolled (internal state). */
  value?: string;
  onChange?: (q: string) => void;
  onSearch: (q: string) => void;
  onClear: () => void;
  /** Render SearchBar's own Search button (default true). Pass false when the
   *  consuming panel already provides its own Search/Clear action row. */
  showSubmitButton?: boolean;
  className?: string;
};

/**
 * Search input shared by the widget Search panels (Headlines, Firehose,
 * StocksRatings, ResearchHeadlines). Mirrors the visual of the watchlist/filter
 * search bar: magnifying-glass on the left, in-input X to clear on the right.
 * Searches fire only on Enter or the Search button — never on keystroke — to
 * avoid a request per character. Submit always fires (even when empty) so a
 * date-only search can run; the consuming form decides whether there are enough
 * criteria to actually search. Optionally controllable via value/onChange.
 */
function SearchBar(props: Props) {
  const showSubmitButton = props.showSubmitButton ?? true;
  const [internalQuery, setInternalQuery] = useState("");
  const query = props.value !== undefined ? props.value : internalQuery;
  const updateQuery = (v: string) => {
    if (props.value === undefined) setInternalQuery(v);
    props.onChange?.(v);
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    // Explicit submit (Enter or Search button). We always call onSearch — even
    // on an empty box — so a date-only (no text) search can run. The consuming
    // form decides whether there are enough criteria (e.g. a date range) to
    // actually search.
    props.onSearch(query);
  };

  const clear = () => {
    updateQuery("");
    // Fire onClear directly so the panel returns to its live feed. (There is no
    // longer a debounce to fire it for us after the text clears.)
    props.onClear();
  };

  return (
    <form
      onSubmit={handleSubmit}
      className={clsx("flex items-center gap-2", props.className)}
    >
      <div className="relative flex-1">
        <MagnifyingGlassIcon className="absolute left-2.5 top-1/2 -translate-y-1/2 size-3.5 text-gray-500 pointer-events-none" />
        <input
          type="text"
          className="w-full pl-8 pr-8 py-1.5 text-xs bg-gray-900 text-gray-200 border border-gray-700 rounded focus:outline-none focus:border-squawk-blue placeholder:text-gray-500"
          value={query}
          onChange={(e) => updateQuery(e.target.value)}
          placeholder="Search..."
          autoFocus
        />
        {query && (
          <button
            type="button"
            onClick={clear}
            className="absolute right-2 top-1/2 -translate-y-1/2 p-0.5 text-gray-500 hover:text-gray-300 transition-colors"
            aria-label="Clear search"
          >
            <XMarkIcon className="size-3" />
          </button>
        )}
      </div>
      {showSubmitButton && (
        <Button
          type="submit"
          size="xs"
          variant="secondary"
          loading={props.loading}
          disabled={props.loading}
        >
          Search
        </Button>
      )}
    </form>
  );
}

export default SearchBar;
